import { createOntologyHeaders } from '../api/core'
import { getOntologyServiceBaseUrl } from '../config'
import {
  mergePendingAgentActionsFromServer,
  type GraphWriteEnvelopeDetail,
  type InFlightAgentActionProposal,
  type InFlightBindWritebackToolProposal,
  type InFlightCreateBranchProposal,
  type InFlightCreateWritebackToolProposal,
  type InFlightConditionVocabularyProposal,
  type InFlightCustomAgentConfigUpdateProposal,
  type InFlightCypherWriteProposal,
  type InFlightGraphWriteProposal,
  type InFlightIndicatesProposal,
  type InFlightServicenowIncidentProposal,
  type InFlightTagHistorizationProposal,
  type InFlightTagWritePolicyProposal,
  type InFlightTagWriteProposal,
  type TagHistorizationExcludedTag,
  type TagHistorizationOperation,
  type TagHistorizationTag
} from '../slices/agent-orchestrator-slice'
import type { AppDispatch } from '../store'

/** Wire shape from GET /api/ontology/agent-writes/pending */
export interface PendingAgentWriteRow {
  id: string
  conversationId: string
  toolUseId: string
  /** Empty / absent for non-graph actions (e.g. servicenow_incident). */
  cypher?: string
  params?: Record<string, unknown>
  summary: string
  /** Graph-only; absent for non-graph actions. */
  changeKind?: string
  /** Top-level discriminator selecting the registered capability. */
  actionKind?: string
  planId?: string
  planIndex?: number
  planSize?: number
  submittedAt: string
  state?: string
}

function camelKeyToSnake(key: string): string {
  if (key.includes('_')) return key
  return key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
}

/** Shallow snake_case for one statement — ClaudeClient camelCases nested keys like changeKind. */
export function snakeifyStatementKeys(record: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(record)) {
    out[camelKeyToSnake(key)] = value
  }
  return out
}

/** Matches the server convention in proposeCypherWritesServer (index 0 uses the parent id). */
export function batchEntryToolUseId(parentToolUseId: string, index: number): string {
  return index === 0 ? parentToolUseId : `${parentToolUseId}#${index}`
}

function readString(record: Record<string, unknown>, key: string): string | null {
  const v = record[key]
  return typeof v === 'string' ? v : null
}

function readSnakeOrCamelString(record: Record<string, unknown>, snakeKey: string): string | null {
  const direct = readString(record, snakeKey)
  if (direct !== null) return direct
  const camelKey = snakeKey.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase())
  return readString(record, camelKey)
}

function readStringArray(record: Record<string, unknown>, key: string): string[] {
  const value = record[key]
  if (!Array.isArray(value)) return []
  return value.filter(
    (entry): entry is string => typeof entry === 'string' && entry.trim().length > 0
  )
}

function parseBatchParent(toolUseId: string): {
  parentId: string
  index: number
} {
  const hashIdx = toolUseId.indexOf('#')
  if (hashIdx === -1) return { parentId: toolUseId, index: 0 }
  const parentId = toolUseId.slice(0, hashIdx)
  const index = Number.parseInt(toolUseId.slice(hashIdx + 1), 10)
  return { parentId, index: Number.isFinite(index) ? index : 0 }
}

function readEnvelopeArms(obj: Record<string, unknown>): GraphWriteEnvelopeDetail {
  const envelope: GraphWriteEnvelopeDetail = {}
  if (Array.isArray(obj.nodes)) envelope.nodes = obj.nodes as GraphWriteEnvelopeDetail['nodes']
  if (Array.isArray(obj.relationships)) {
    envelope.relationships = obj.relationships as GraphWriteEnvelopeDetail['relationships']
  }
  if (Array.isArray(obj.deletes))
    envelope.deletes = obj.deletes as GraphWriteEnvelopeDetail['deletes']
  if (Array.isArray(obj.overwrites)) {
    envelope.overwrites = obj.overwrites as GraphWriteEnvelopeDetail['overwrites']
  }
  return envelope
}

function envelopeHasAnyArm(envelope: GraphWriteEnvelopeDetail): boolean {
  return (
    (envelope.nodes?.length ?? 0) +
      (envelope.relationships?.length ?? 0) +
      (envelope.deletes?.length ?? 0) +
      (envelope.overwrites?.length ?? 0) >
    0
  )
}

/**
 * Build optimistic in-flight proposals from `propose_cypher_writes` tool
 * input (`writes[]` structured envelopes → `kind: 'graph_write'`).
 */
export function buildCypherWriteEntriesFromToolInput(
  parentToolUseId: string,
  input: unknown,
  startedAtMs: number
): { id: string; proposal: InFlightGraphWriteProposal }[] {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return []
  const obj = input as Record<string, unknown>
  const writes = obj.writes
  if (!Array.isArray(writes)) return []
  const batchSummary = readString(obj, 'summary') ?? ''
  const out: { id: string; proposal: InFlightGraphWriteProposal }[] = []
  writes.forEach((entry, index) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return
    const sObj = snakeifyStatementKeys(entry as Record<string, unknown>)
    const summary = readString(sObj, 'summary')
    const changeKind = readSnakeOrCamelString(sObj, 'change_kind')
    if (summary === null || changeKind === null) return
    const envelope = readEnvelopeArms(sObj)
    if (!envelopeHasAnyArm(envelope)) return
    out.push({
      id: batchEntryToolUseId(parentToolUseId, index),
      proposal: {
        kind: 'graph_write',
        envelope,
        summary,
        changeKind,
        batchParentId: parentToolUseId,
        batchIndex: index,
        batchSize: writes.length,
        batchSummary: batchSummary || undefined,
        startedAtMs
      }
    })
  })
  return out
}

function evidenceEntrySummary(nodeId: string, valid: boolean | undefined): string {
  if (valid === false) return `Record evidence and mark node invalid: ${nodeId}`
  if (valid === true) return `Record evidence and mark node valid: ${nodeId}`
  return `Record evidence on node: ${nodeId}`
}

/**
 * Build optimistic in-flight proposals from the `propose_node_evidence`
 * tool input (one `cypher_write`-kind card per `evidence[]` entry,
 * mirroring the server's per-item `agent_writes` rows + `#index`
 * tool-use-id convention). The blocking propose stalls the turn until
 * the user decides, so the cards must exist before any post-stream
 * hydrate runs — the hydrate poll then replaces them with the canonical
 * rows (resolved display names, planId, writeId).
 */
export function buildNodeEvidenceEntriesFromToolInput(
  parentToolUseId: string,
  input: unknown,
  startedAtMs: number
): { id: string; proposal: InFlightCypherWriteProposal }[] {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return []
  const obj = input as Record<string, unknown>
  const evidence = obj.evidence
  if (!Array.isArray(evidence)) return []
  const batchSummary = readString(obj, 'summary') ?? ''
  const out: { id: string; proposal: InFlightCypherWriteProposal }[] = []
  evidence.forEach((entry, index) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return
    const eObj = snakeifyStatementKeys(entry as Record<string, unknown>)
    const nodeId = readSnakeOrCamelString(eObj, 'node_id')
    const note = readString(eObj, 'note')
    if (nodeId === null || note === null) return
    const valid = typeof eObj.valid === 'boolean' ? eObj.valid : undefined
    out.push({
      id: batchEntryToolUseId(parentToolUseId, index),
      proposal: {
        kind: 'cypher_write',
        cypher: '',
        params: { nodeId, note, ...(valid !== undefined ? { valid } : {}) },
        summary: evidenceEntrySummary(nodeId, valid),
        changeKind: 'node_evidence',
        batchParentId: parentToolUseId,
        batchIndex: index,
        batchSize: evidence.length,
        batchSummary: batchSummary || undefined,
        startedAtMs
      }
    })
  })
  return out
}

/** ServiceNow create-incident input keys that are NOT optional pass-through fields. */
const SERVICENOW_RESERVED_INPUT_KEYS = new Set(['integration_id', 'short_description'])

/** Collect the optional ServiceNow create fields (string values) off a record. */
function readServicenowFields(record: Record<string, unknown>): Record<string, string> {
  const fields: Record<string, string> = {}
  for (const [key, value] of Object.entries(record)) {
    if (SERVICENOW_RESERVED_INPUT_KEYS.has(key)) continue
    if (typeof value === 'string' && value.trim().length > 0) fields[key] = value
  }
  return fields
}

/**
 * Build the in-flight ServiceNow incident proposal from the
 * `create_servicenow_incident` tool input (snake-cased top-level keys).
 * Returns `null` when `short_description` is missing.
 */
export function buildServicenowIncidentEntryFromToolInput(
  parentToolUseId: string,
  input: unknown,
  startedAtMs: number
): { id: string; proposal: InFlightServicenowIncidentProposal } | null {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return null
  const obj = input as Record<string, unknown>
  const shortDescription = readString(obj, 'short_description')
  if (shortDescription === null || shortDescription.trim().length === 0) return null
  const integrationId = readString(obj, 'integration_id') ?? undefined
  return {
    id: parentToolUseId,
    proposal: {
      kind: 'servicenow_incident',
      summary: `Create ServiceNow incident: "${shortDescription}"`,
      shortDescription,
      integrationId,
      fields: readServicenowFields(obj),
      startedAtMs
    }
  }
}

function readTagWritePrimitive(value: unknown): number | string | boolean | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' || typeof value === 'boolean') return value
  return null
}

/**
 * Build optimistic approval cards from `propose_tag_write` input. One card is
 * created per item using the same `<parent>#<index>` convention as the server.
 */
export function buildTagWriteEntriesFromToolInput(
  parentToolUseId: string,
  input: unknown,
  startedAtMs: number
): { id: string; proposal: InFlightTagWriteProposal }[] {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return []
  const writes = (input as Record<string, unknown>).writes
  if (!Array.isArray(writes)) return []
  const out: { id: string; proposal: InFlightTagWriteProposal }[] = []
  writes.forEach((entry, index) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return
    const item = entry as Record<string, unknown>
    const tagId = readSnakeOrCamelString(item, 'tag_id')
    const value = readTagWritePrimitive(item.value)
    const reason = readString(item, 'reason')
    if (!tagId?.trim() || value === null || !reason?.trim()) return
    const expiryMinutesRaw = item.expiry_minutes ?? item.expiryMinutes
    const expiryMinutes =
      typeof expiryMinutesRaw === 'number' && Number.isInteger(expiryMinutesRaw)
        ? expiryMinutesRaw
        : 15
    const revertRaw = item.revert_condition ?? item.revertCondition
    const revertCondition =
      revertRaw && typeof revertRaw === 'object' && !Array.isArray(revertRaw)
        ? (revertRaw as Record<string, unknown>)
        : undefined
    const expiresAt = new Date(startedAtMs + expiryMinutes * 60_000).toISOString()
    out.push({
      id: batchEntryToolUseId(parentToolUseId, index),
      proposal: {
        kind: 'tag_write',
        summary: `Write ${tagId}: ${reason}`,
        tagId,
        proposedValue: value,
        reason,
        expiresAt,
        ...(revertCondition ? { revertCondition } : {}),
        batchParentId: parentToolUseId,
        batchIndex: index,
        batchSize: writes.length,
        startedAtMs
      }
    })
  })
  return out
}

export function buildTagWritePolicyEntryFromToolInput(
  parentToolUseId: string,
  input: unknown,
  startedAtMs: number
): { id: string; proposal: InFlightTagWritePolicyProposal } | null {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return null
  const item = input as Record<string, unknown>
  const tagId = readSnakeOrCamelString(item, 'tag_id')
  const disposition = readString(item, 'disposition')
  const reason = readString(item, 'reason')
  if (
    !tagId?.trim() ||
    (disposition !== 'human' && disposition !== 'ai_propose' && disposition !== 'ai_auto') ||
    !reason?.trim()
  ) {
    return null
  }
  const summary =
    disposition === 'human'
      ? `Allow operator writes to ${tagId}`
      : disposition === 'ai_propose'
        ? `Allow agent-proposed writes to ${tagId}`
        : `Allow autonomous AI writes to ${tagId}`
  return {
    id: parentToolUseId,
    proposal: {
      kind: 'tag_write_policy',
      summary,
      tagId,
      registryId: '',
      reason,
      currentPolicy: { disposition: 'resolving' },
      proposedPolicy: { disposition },
      startedAtMs
    }
  }
}

function optionalRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined
}

function tagHistorizationStartSummary(
  operation: TagHistorizationOperation,
  input: Record<string, unknown>
): string {
  if (operation === 'enable') return 'Start recording history'
  if (operation === 'disable') return 'Stop recording history'
  return input.target === 'webdev_poll'
    ? 'Switch tags to WebDev poll'
    : 'Switch tags to OPC subscribe'
}

/**
 * Optimistic card shown while the propose tool resolves the selection; the
 * pending row replaces it with the resolved buckets once it is persisted.
 */
export function buildTagHistorizationEntryFromToolInput(
  parentToolUseId: string,
  operation: TagHistorizationOperation,
  input: unknown,
  startedAtMs: number
): { id: string; proposal: InFlightTagHistorizationProposal } | null {
  const item = optionalRecord(input)
  const reason = item ? readString(item, 'reason') : null
  if (!item || !reason?.trim()) return null
  return {
    id: parentToolUseId,
    proposal: {
      kind: 'tag_historization',
      operation,
      summary: tagHistorizationStartSummary(operation, item),
      reason,
      resolving: true,
      requiredPermissions: [],
      toRegister: [],
      toEnable: [],
      toDisable: [],
      convertTags: [],
      unchangedCount: 0,
      excluded: [],
      startedAtMs
    }
  }
}

function readHistorizationTags(value: unknown): TagHistorizationTag[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((entry) => {
    const row = optionalRecord(entry)
    const nodeId = row ? readString(row, 'node_id') : null
    return row && nodeId ? [{ nodeId, tagPath: readString(row, 'tag_path') ?? '' }] : []
  })
}

function readHistorizationExcluded(value: unknown): TagHistorizationExcludedTag[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((entry) => {
    const row = optionalRecord(entry)
    if (!row) return []
    return [
      {
        nodeId: readString(row, 'node_id') ?? '',
        tagPath: readString(row, 'tag_path') ?? '',
        reasonCode: readString(row, 'reason_code') ?? '',
        reason: readString(row, 'reason') ?? ''
      }
    ]
  })
}

function pendingTagHistorizationToEntry(write: PendingAgentWriteRow): {
  id: string
  proposal: InFlightTagHistorizationProposal
} {
  const params = write.params ?? {}
  const operation: TagHistorizationOperation =
    params.operation === 'disable' || params.operation === 'change_source'
      ? params.operation
      : 'enable'
  const registerOptions = optionalRecord(params.register_options)
  const registerTransport = registerOptions ? readString(registerOptions, 'transport') : null
  const registerUrl = registerOptions
    ? readString(registerOptions, registerTransport === 'opc' ? 'opc_endpoint_url' : 'gateway_url')
    : null
  const source = optionalRecord(params.source)
  const target = optionalRecord(params.target)
  const startedAtMs = Date.parse(write.submittedAt)
  return {
    id: write.toolUseId,
    proposal: {
      kind: 'tag_historization',
      operation,
      summary: write.summary,
      reason: typeof params.reason === 'string' ? params.reason : write.summary,
      requiredPermissions: readStringArray(params, 'required_permissions'),
      toRegister: readHistorizationTags(params.to_register),
      toEnable: readHistorizationTags(params.to_enable),
      toDisable: readHistorizationTags(params.to_disable),
      convertTags: readHistorizationTags(params.tags),
      ...(registerTransport && registerUrl
        ? { registerDestination: { transport: registerTransport, url: registerUrl } }
        : {}),
      ...(source
        ? {
            source: {
              sourceUrl: readString(source, 'source_url') ?? '',
              scrapeMode: readString(source, 'scrape_mode') ?? ''
            }
          }
        : {}),
      ...(target
        ? {
            target: {
              scrapeMode: readString(target, 'scrape_mode') ?? '',
              destinationUrl: readString(target, 'destination_url') ?? '',
              credentialPosture: readString(target, 'credential_posture')
            }
          }
        : {}),
      unchangedCount: typeof params.unchanged_count === 'number' ? params.unchanged_count : 0,
      excluded: readHistorizationExcluded(params.excluded),
      startedAtMs: Number.isFinite(startedAtMs) ? startedAtMs : Date.now(),
      planId: write.planId,
      writeId: write.id
    }
  }
}

function pendingServicenowIncidentToEntry(write: PendingAgentWriteRow): {
  id: string
  proposal: InFlightServicenowIncidentProposal
} {
  const params = write.params ?? {}
  const shortDescription =
    typeof params.short_description === 'string' && params.short_description.trim().length > 0
      ? params.short_description
      : write.summary
  const integrationId =
    typeof params.integration_id === 'string' ? params.integration_id : undefined
  const startedAtMs = Date.parse(write.submittedAt)
  return {
    id: write.toolUseId,
    proposal: {
      kind: 'servicenow_incident',
      summary: write.summary,
      shortDescription,
      integrationId,
      fields: readServicenowFields(params),
      startedAtMs: Number.isFinite(startedAtMs) ? startedAtMs : Date.now(),
      planId: write.planId,
      writeId: write.id
    }
  }
}

/**
 * Build the in-flight create-branch proposal from the
 * `propose_create_branch` tool input. Returns `null` when `name` is missing.
 */
export function buildCreateBranchEntryFromToolInput(
  parentToolUseId: string,
  input: unknown,
  startedAtMs: number
): { id: string; proposal: InFlightCreateBranchProposal } | null {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return null
  const obj = input as Record<string, unknown>
  const name = readString(obj, 'name')
  if (name === null || name.trim().length === 0) return null
  const reason = readString(obj, 'reason') ?? undefined
  // The tool input carries no fork source; the persisted row does (the
  // hydrate pass fills sourceName from it).
  return {
    id: parentToolUseId,
    proposal: {
      kind: 'create_branch',
      summary: reason ? `Create branch "${name}" — ${reason}` : `Create branch "${name}"`,
      branchName: name,
      reason,
      startedAtMs
    }
  }
}

/**
 * Build the in-flight create-writeback-tool proposal from the
 * `add_db_write_tool` tool input. Returns `null` when `name` or the
 * parameterized query is missing.
 */
export function buildCreateWritebackToolEntryFromToolInput(
  parentToolUseId: string,
  input: unknown,
  startedAtMs: number
): { id: string; proposal: InFlightCreateWritebackToolProposal } | null {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return null
  const obj = input as Record<string, unknown>
  const name = readSnakeOrCamelString(obj, 'name')
  const parametrizedQuery = readSnakeOrCamelString(obj, 'parametrized_query')
  if (name === null || name.trim().length === 0) return null
  if (parametrizedQuery === null || parametrizedQuery.trim().length === 0) return null
  const reason = readSnakeOrCamelString(obj, 'reason') ?? ''
  const connectionId = readSnakeOrCamelString(obj, 'connection_id') ?? ''
  const configId = readSnakeOrCamelString(obj, 'config_id') ?? undefined
  const configName = readSnakeOrCamelString(obj, 'config_name') ?? undefined
  const params = readStringArray(obj, 'params')
  return {
    id: parentToolUseId,
    proposal: {
      kind: 'create_writeback_tool',
      summary: configId
        ? `Create DB writeback tool "${name}" and bind to agent ${configName ?? configId} — ${reason}`
        : `Create DB writeback tool "${name}" — ${reason}`,
      name,
      toolKind: 'db',
      parametrizedQuery,
      params,
      connectionId,
      reason,
      configId,
      ...(configName ? { configName } : {}),
      startedAtMs
    }
  }
}

/**
 * Build the in-flight bind-writeback-tool proposal from the
 * `use_writeback_tool` tool input. Returns `null` when `tool_id` or
 * `config_id` is missing.
 */
export function buildBindWritebackToolEntryFromToolInput(
  parentToolUseId: string,
  input: unknown,
  startedAtMs: number
): { id: string; proposal: InFlightBindWritebackToolProposal } | null {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return null
  const obj = input as Record<string, unknown>
  const toolId = readSnakeOrCamelString(obj, 'tool_id')
  const configId = readSnakeOrCamelString(obj, 'config_id')
  if (toolId === null || toolId.trim().length === 0) return null
  if (configId === null || configId.trim().length === 0) return null
  const reason = readSnakeOrCamelString(obj, 'reason') ?? ''
  const name = readSnakeOrCamelString(obj, 'name') ?? undefined
  const configName = readSnakeOrCamelString(obj, 'config_name') ?? undefined
  return {
    id: parentToolUseId,
    proposal: {
      kind: 'bind_writeback_tool',
      summary: name
        ? `Bind db_write_${name} to agent ${configName ?? configId} — ${reason}`
        : `Bind writeback tool ${toolId} to agent ${configName ?? configId} — ${reason}`,
      toolId,
      configId,
      reason,
      ...(name ? { name } : {}),
      ...(configName ? { configName } : {}),
      startedAtMs
    }
  }
}

function pendingCreateWritebackToolToEntry(write: PendingAgentWriteRow): {
  id: string
  proposal: InFlightCreateWritebackToolProposal
} {
  const params = write.params ?? {}
  const name =
    typeof params.name === 'string' && params.name.trim().length > 0 ? params.name : write.summary
  const parametrizedQuery =
    typeof params.parametrized_query === 'string' ? params.parametrized_query : ''
  const connectionId = typeof params.connection_id === 'string' ? params.connection_id : ''
  const reason = typeof params.reason === 'string' ? params.reason : ''
  const configId = typeof params.config_id === 'string' ? params.config_id : undefined
  const configName =
    typeof params.config_name === 'string' && params.config_name.trim()
      ? params.config_name
      : undefined
  const queryParams = Array.isArray(params.params)
    ? params.params.filter((entry): entry is string => typeof entry === 'string')
    : []
  const startedAtMs = Date.parse(write.submittedAt)
  return {
    id: write.toolUseId,
    proposal: {
      kind: 'create_writeback_tool',
      summary: write.summary,
      name,
      toolKind: 'db',
      parametrizedQuery,
      params: queryParams,
      connectionId,
      reason,
      configId,
      ...(configName ? { configName } : {}),
      startedAtMs: Number.isFinite(startedAtMs) ? startedAtMs : Date.now(),
      planId: write.planId,
      writeId: write.id
    }
  }
}

function pendingBindWritebackToolToEntry(write: PendingAgentWriteRow): {
  id: string
  proposal: InFlightBindWritebackToolProposal
} {
  const params = write.params ?? {}
  const toolId = typeof params.tool_id === 'string' ? params.tool_id : ''
  const configId = typeof params.config_id === 'string' ? params.config_id : ''
  const reason = typeof params.reason === 'string' ? params.reason : ''
  const name = typeof params.name === 'string' && params.name.trim() ? params.name : undefined
  const configName =
    typeof params.config_name === 'string' && params.config_name.trim()
      ? params.config_name
      : undefined
  const startedAtMs = Date.parse(write.submittedAt)
  return {
    id: write.toolUseId,
    proposal: {
      kind: 'bind_writeback_tool',
      summary: write.summary,
      toolId,
      configId,
      reason,
      ...(name ? { name } : {}),
      ...(configName ? { configName } : {}),
      startedAtMs: Number.isFinite(startedAtMs) ? startedAtMs : Date.now(),
      planId: write.planId,
      writeId: write.id
    }
  }
}

function pendingCreateBranchToEntry(write: PendingAgentWriteRow): {
  id: string
  proposal: InFlightCreateBranchProposal
} {
  const params = write.params ?? {}
  const branchName =
    typeof params.name === 'string' && params.name.trim().length > 0 ? params.name : write.summary
  const reason = typeof params.reason === 'string' ? params.reason : undefined
  const sourceName = typeof params.source_name === 'string' ? params.source_name : undefined
  const startedAtMs = Date.parse(write.submittedAt)
  return {
    id: write.toolUseId,
    proposal: {
      kind: 'create_branch',
      summary: write.summary,
      branchName,
      reason,
      sourceName,
      startedAtMs: Number.isFinite(startedAtMs) ? startedAtMs : Date.now(),
      planId: write.planId,
      writeId: write.id
    }
  }
}

function pendingTagWriteToEntry(write: PendingAgentWriteRow): {
  id: string
  proposal: InFlightTagWriteProposal
} {
  const { parentId, index } = parseBatchParent(write.toolUseId)
  const batchIndex =
    write.planIndex !== undefined && write.planIndex > 0 ? write.planIndex - 1 : index
  const params = write.params ?? {}
  const tagId = typeof params.tag_id === 'string' ? params.tag_id : ''
  const proposedValue = readTagWritePrimitive(params.value) ?? ''
  const reason = typeof params.reason === 'string' ? params.reason : write.summary
  const expiresAt = typeof params.expires_at === 'string' ? params.expires_at : undefined
  const revertCondition =
    params.revert_condition &&
    typeof params.revert_condition === 'object' &&
    !Array.isArray(params.revert_condition)
      ? (params.revert_condition as Record<string, unknown>)
      : undefined
  const target =
    params.target && typeof params.target === 'object' && !Array.isArray(params.target)
      ? (params.target as Record<string, unknown>)
      : undefined
  const trend = Array.isArray(params.trend) ? params.trend : undefined
  const startedAtMs = Date.parse(write.submittedAt)
  return {
    id: write.toolUseId,
    proposal: {
      kind: 'tag_write',
      summary: write.summary,
      tagId,
      proposedValue,
      reason,
      ...(expiresAt ? { expiresAt } : {}),
      ...(revertCondition ? { revertCondition } : {}),
      ...(target ? { target } : {}),
      ...(params.current_value !== undefined ? { currentValue: params.current_value } : {}),
      ...(params.policy_snapshot !== undefined ? { policySnapshot: params.policy_snapshot } : {}),
      ...(trend ? { trend } : {}),
      batchParentId: parentId,
      batchIndex,
      batchSize: write.planSize,
      startedAtMs: Number.isFinite(startedAtMs) ? startedAtMs : Date.now(),
      planId: write.planId,
      writeId: write.id
    }
  }
}

function pendingTagWritePolicyToEntry(write: PendingAgentWriteRow): {
  id: string
  proposal: InFlightTagWritePolicyProposal
} {
  const params = write.params ?? {}
  const tagId = typeof params.tag_id === 'string' ? params.tag_id : ''
  const registryId = typeof params.registry_id === 'string' ? params.registry_id : ''
  const reason = typeof params.reason === 'string' ? params.reason : write.summary
  const target =
    params.target && typeof params.target === 'object' && !Array.isArray(params.target)
      ? (params.target as Record<string, unknown>)
      : undefined
  const currentPolicy =
    params.current_policy &&
    typeof params.current_policy === 'object' &&
    !Array.isArray(params.current_policy)
      ? (params.current_policy as Record<string, unknown>)
      : { disposition: 'read_only' }
  const proposedPolicy =
    params.proposed_policy &&
    typeof params.proposed_policy === 'object' &&
    !Array.isArray(params.proposed_policy)
      ? (params.proposed_policy as Record<string, unknown>)
      : { ...currentPolicy, disposition: 'human' }
  const startedAtMs = Date.parse(write.submittedAt)
  return {
    id: write.toolUseId,
    proposal: {
      kind: 'tag_write_policy',
      summary: write.summary,
      tagId,
      registryId,
      reason,
      ...(target ? { target } : {}),
      currentPolicy,
      proposedPolicy,
      startedAtMs: Number.isFinite(startedAtMs) ? startedAtMs : Date.now(),
      planId: write.planId,
      writeId: write.id
    }
  }
}

function pendingGraphWriteToEntry(write: PendingAgentWriteRow): {
  id: string
  proposal: InFlightGraphWriteProposal
} {
  const { parentId, index } = parseBatchParent(write.toolUseId)
  const batchIndex =
    write.planIndex !== undefined && write.planIndex > 0 ? write.planIndex - 1 : index
  const startedAtMs = Date.parse(write.submittedAt)
  const params = write.params ?? {}
  const rawEnvelope = params.envelope
  const envelope =
    rawEnvelope && typeof rawEnvelope === 'object' && !Array.isArray(rawEnvelope)
      ? readEnvelopeArms(rawEnvelope as Record<string, unknown>)
      : {}
  return {
    id: write.toolUseId,
    proposal: {
      kind: 'graph_write',
      envelope,
      summary: write.summary,
      changeKind: write.changeKind ?? '',
      batchParentId: parentId,
      batchIndex,
      batchSize: write.planSize,
      startedAtMs: Number.isFinite(startedAtMs) ? startedAtMs : Date.now(),
      planId: write.planId,
      writeId: write.id
    }
  }
}

function pendingUpdateCustomAgentConfigToEntry(write: PendingAgentWriteRow): {
  id: string
  proposal: InFlightCustomAgentConfigUpdateProposal
} {
  const params = write.params ?? {}
  const patch =
    params.patch && typeof params.patch === 'object' && !Array.isArray(params.patch)
      ? (params.patch as Record<string, unknown>)
      : {}
  const configId = typeof params.config_id === 'string' ? params.config_id : ''
  const prompt = typeof patch.prompt === 'string' ? patch.prompt : undefined
  const triggers = Array.isArray(patch.triggers) ? patch.triggers : undefined
  const triggerEvents = Array.isArray(patch.trigger_events)
    ? patch.trigger_events.filter((event): event is string => typeof event === 'string')
    : undefined
  const cron = typeof patch.cron === 'string' ? patch.cron : undefined
  const triggerCooldownMs =
    typeof patch.trigger_cooldown_ms === 'number' ? patch.trigger_cooldown_ms : undefined
  const analysisWindowMs =
    typeof patch.analysis_window_ms === 'number' ? patch.analysis_window_ms : undefined
  const startedAtMs = Date.parse(write.submittedAt)
  return {
    id: write.toolUseId,
    proposal: {
      kind: 'update_custom_agent_config',
      configId,
      summary: write.summary,
      ...(prompt !== undefined ? { prompt } : {}),
      ...(triggers ? { triggers } : {}),
      ...(triggerEvents ? { triggerEvents } : {}),
      ...(cron !== undefined ? { cron } : {}),
      ...(triggerCooldownMs !== undefined ? { triggerCooldownMs } : {}),
      ...(analysisWindowMs !== undefined ? { analysisWindowMs } : {}),
      startedAtMs: Number.isFinite(startedAtMs) ? startedAtMs : Date.now(),
      planId: write.planId,
      writeId: write.id
    }
  }
}

function proposalParams(write: PendingAgentWriteRow): Record<string, unknown> {
  const raw = write.params?.proposal
  return raw && typeof raw === 'object' && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : {}
}

function pendingConditionVocabularyToEntry(write: PendingAgentWriteRow): {
  id: string
  proposal: InFlightConditionVocabularyProposal
} {
  const params = proposalParams(write)
  const startedAtMs = Date.parse(write.submittedAt)
  return {
    id: write.toolUseId,
    proposal: {
      kind: 'condition_vocabulary',
      conditionId: readSnakeOrCamelString(params, 'condition_id') ?? '',
      name: readString(params, 'name') ?? '',
      conditionKind: readString(params, 'kind') ?? 'other',
      perceivableBy: readStringArray(params, 'perceivable_by'),
      equipmentType: readSnakeOrCamelString(params, 'equipment_type') ?? '',
      howToCheck: readSnakeOrCamelString(params, 'how_to_check') ?? '',
      summary: write.summary,
      startedAtMs: Number.isFinite(startedAtMs) ? startedAtMs : Date.now(),
      planId: write.planId,
      writeId: write.id
    }
  }
}

function pendingIndicatesToEntry(write: PendingAgentWriteRow): {
  id: string
  proposal: InFlightIndicatesProposal
} {
  const params = proposalParams(write)
  const startedAtMs = Date.parse(write.submittedAt)
  const rangeLow = params.range_low ?? params.rangeLow
  const rangeHigh = params.range_high ?? params.rangeHigh
  return {
    id: write.toolUseId,
    proposal: {
      kind: 'indicates',
      instrumentId: readSnakeOrCamelString(params, 'instrument_id') ?? '',
      observableId: readSnakeOrCamelString(params, 'observable_id') ?? '',
      ...(readString(params, 'display') ? { display: readString(params, 'display')! } : {}),
      ...(typeof rangeLow === 'number' ? { rangeLow } : {}),
      ...(typeof rangeHigh === 'number' ? { rangeHigh } : {}),
      ...(readString(params, 'source') ? { source: readString(params, 'source')! } : {}),
      summary: write.summary,
      startedAtMs: Number.isFinite(startedAtMs) ? startedAtMs : Date.now(),
      planId: write.planId,
      writeId: write.id
    }
  }
}

function pendingCypherWriteToEntry(write: PendingAgentWriteRow): {
  id: string
  proposal: InFlightCypherWriteProposal
} {
  const { parentId, index } = parseBatchParent(write.toolUseId)
  const batchIndex =
    write.planIndex !== undefined && write.planIndex > 0 ? write.planIndex - 1 : index
  const startedAtMs = Date.parse(write.submittedAt)
  return {
    id: write.toolUseId,
    proposal: {
      kind: 'cypher_write',
      cypher: write.cypher ?? '',
      params: write.params ?? {},
      summary: write.summary,
      changeKind: write.changeKind ?? '',
      batchParentId: parentId,
      batchIndex,
      batchSize: write.planSize,
      startedAtMs: Number.isFinite(startedAtMs) ? startedAtMs : Date.now(),
      planId: write.planId,
      writeId: write.id
    }
  }
}

/**
 * Map one pending `agent_writes` row to its in-flight proposal, dispatching
 * on `actionKind` so a single hydrate pass restores cards for every
 * capability. Falls back to the cypher-write shape for legacy rows that
 * predate `actionKind`.
 */
export function pendingAgentWriteToEntry(write: PendingAgentWriteRow): {
  id: string
  proposal: InFlightAgentActionProposal
} {
  if (write.actionKind === 'custom_agent_run') {
    const params = write.params ?? {}
    return {
      id: write.toolUseId,
      proposal: {
        kind: 'custom_agent_run',
        summary: write.summary,
        name: typeof params.name === 'string' ? params.name : 'Custom agent',
        reason: typeof params.reason === 'string' ? params.reason : '',
        parameters: params.parameters as Record<string, unknown> | undefined,
        instruction: typeof params.instruction === 'string' ? params.instruction : undefined,
        parentRunId: typeof params.parent_run_id === 'string' ? params.parent_run_id : undefined,
        itemCount: typeof params.item_count === 'number' ? params.item_count : undefined,
        accepted: write.state === 'processing',
        planId: write.planId,
        writeId: write.id,
        startedAtMs: Date.parse(write.submittedAt)
      }
    }
  }
  if (write.actionKind === 'servicenow_incident') {
    return pendingServicenowIncidentToEntry(write)
  }
  if (write.actionKind === 'create_branch') {
    return pendingCreateBranchToEntry(write)
  }
  if (write.actionKind === 'create_writeback_tool') {
    return pendingCreateWritebackToolToEntry(write)
  }
  if (write.actionKind === 'bind_writeback_tool') {
    return pendingBindWritebackToolToEntry(write)
  }
  if (write.actionKind === 'tag_write') {
    return pendingTagWriteToEntry(write)
  }
  if (write.actionKind === 'tag_write_policy') {
    return pendingTagWritePolicyToEntry(write)
  }
  if (write.actionKind === 'tag_historization') {
    return pendingTagHistorizationToEntry(write)
  }
  if (write.actionKind === 'graph_write') {
    return pendingGraphWriteToEntry(write)
  }
  if (write.actionKind === 'update_custom_agent_config') {
    return pendingUpdateCustomAgentConfigToEntry(write)
  }
  if (write.actionKind === 'condition_vocabulary') {
    return pendingConditionVocabularyToEntry(write)
  }
  if (write.actionKind === 'indicates') {
    return pendingIndicatesToEntry(write)
  }
  return pendingCypherWriteToEntry(write)
}

export async function fetchPendingAgentWrites(
  conversationId: string
): Promise<PendingAgentWriteRow[]> {
  const url = `${getOntologyServiceBaseUrl()}/agent-writes/pending?conversation_id=${encodeURIComponent(conversationId)}`
  const response = await fetch(url, { headers: createOntologyHeaders() })
  if (!response.ok) {
    throw new Error(`Failed to load pending cypher writes (${response.status})`)
  }
  const text = await response.text()
  if (!text.trim()) return []
  const body = JSON.parse(text) as { writes?: PendingAgentWriteRow[] }
  return Array.isArray(body.writes) ? body.writes : []
}

/**
 * Merge any `awaiting_user` agent_writes rows for this conversation into
 * sidebar state. Used as a fallback when SSE parsing misses nested camelCase
 * fields, and to restore proposals after refresh / stream completion while
 * the server-side tool is still blocked on user input.
 *
 * Targets a specific conversation's tab — pass `conversationId` so the
 * slice can drop rows into the right `tab.inFlightCypherWrites`.
 */
export function hydratePendingCypherWrites(conversationId: string) {
  return async (dispatch: AppDispatch): Promise<number> => {
    const writes = await fetchPendingAgentWrites(conversationId)
    if (writes.length === 0) return 0
    dispatch(
      mergePendingAgentActionsFromServer({
        id: conversationId,
        entries: writes.map((write) => pendingAgentWriteToEntry(write))
      })
    )
    return writes.length
  }
}

const HYDRATE_RETRY_DELAYS_MS = [0, 400, 1200, 2500]

/**
 * Poll the pending-writes endpoint a few times after `tool_call_start` —
 * rows are persisted shortly after the event is emitted. Proposals that
 * resolve a selection before persisting pass a longer `delaysMs`.
 */
export function scheduleHydratePendingCypherWrites(
  dispatch: AppDispatch,
  conversationId: string | null | undefined,
  delaysMs: readonly number[] = HYDRATE_RETRY_DELAYS_MS
): void {
  if (!conversationId) return
  const schedule =
    typeof globalThis.setTimeout === 'function' ? globalThis.setTimeout.bind(globalThis) : null
  if (!schedule) return
  for (const delayMs of delaysMs) {
    const handle = schedule(() => {
      void dispatch(hydratePendingCypherWrites(conversationId)).catch((err) => {
        console.warn('[agent-orchestrator] pending cypher-write hydrate failed', err)
      })
    }, delayMs)
    if (handle && typeof handle === 'object' && 'unref' in handle) {
      ;(handle as NodeJS.Timeout).unref()
    }
  }
}

/**
 * Generic alias for `scheduleHydratePendingCypherWrites`. The pending
 * endpoint returns every `awaiting_user` row for a conversation regardless
 * of `actionKind`, and `pendingAgentWriteToEntry` routes each to the right
 * capability — so a single poll restores cards for all action kinds. Named
 * generically for non-cypher capabilities (e.g. servicenow_incident).
 */
export const scheduleHydratePendingAgentWrites = scheduleHydratePendingCypherWrites
